import React, { FC, useEffect, useState } from 'react';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import { Link } from 'react-router-dom';
import { ITransactionsFilters, TransactionsPageFilter } from '../../components/transactions/TransactionsPageFilter';
import { useDisputesContext } from '../../store/disputes/disputes-context';
import { getList } from '../../store/disputes/actions/disputes';
import { usePaymentContext } from '../../store/payments/payments-context';
import {
	exportCSVTransActionsData,
	fetchTransactionsData,
} from '../../store/payments/actions/transactions';
import { ITransactionsPaginationData } from '../../store/payments/payments-interfaces-helper';
import { ExportCSVButton } from '../../components/form/ExportCSVButton';

export const Transactions: FC = () => {
	const { dispatch: disputeDispatch } = useDisputesContext();
	const {
		paymentContextState: { isExportTransactionCSVLoading },
		dispatch
	} = usePaymentContext();

	const [from, setFrom] = useState<Date | string>(null);
	const [to, setTo] = useState<Date | string>(null);
	const [provider, setProvider] = useState<string[]>([]);
	const [type, setType] = useState<string[]>([]);
	const [status, setStatus] = useState<string[]>([]);
	const [arn, setArn] = useState<string>('');
	const [amount, setAmount] = useState<string>('');
	const [bin, setBin] = useState<string>('');
	const [last4, setLast4] = useState<string>('');
	const [orderId, setOrderId] = useState<string>('');
	const [transactionId, setTransactionId] = useState<string>('');
	const [externalReferenceId, setExternalReferenceId] = useState<string>('');
	const [query, setQuery] = useState<string>('');
	const [pageSize, setPageSize] = useState<string>('10');
	const [pageNumber, setPageNumber] = useState<string>('1');
	const [appliedFiltersState, setAppliedFiltersState] = useState<ITransactionsFilters>(null);

	const [transactionsListData, setTransactionsListData] = useState<ITransactionsPaginationData>(null);
	const [isTransactionsListLoading, setIsTransactionsListLoading] = useState<boolean>(true);

	const initialFilterState: ITransactionsFilters = {
		query,
		from,
		to,
		provider,
		type,
		status,
		arn,
		amount,
		bin,
		last4,
		orderId,
		transactionId,
		externalReferenceId,
		pageNumber,
		pageSize
	};

	useEffect(() => {
		void getList(disputeDispatch);
	}, []);

	const rowActions = (rowData): JSX.Element => (
		<div className="text-center">
			<Link to={`/orders/${rowData?.original?.orderId}`} className="text-muted fs-7 fw-bolder">
				Details
			</Link>
		</div>
	);

	const listener = async (searchParams: ITransactionsFilters): Promise<void> => {
		let localSearchParams: ITransactionsFilters;
		setIsTransactionsListLoading(true);

		if (Object.keys(searchParams).length) {
			localSearchParams = searchParams;

			setFrom(searchParams?.from ?? null);
			setTo(searchParams?.to ?? null);
			setArn(searchParams?.arn ?? '');
			setAmount(searchParams?.amount ?? '');
			setBin(searchParams?.bin ?? '');
			setLast4(searchParams?.last4 ?? '');
			setOrderId(searchParams?.orderId ?? '');
			setTransactionId(searchParams?.transactionId ?? '');
			setExternalReferenceId(searchParams?.externalReferenceId ?? '');
			setQuery(searchParams?.query ?? '');
			setPageSize(searchParams?.pageSize ?? '10');
			setPageNumber(searchParams?.pageNumber ?? '1');

			if (searchParams?.provider && typeof searchParams?.provider === 'string') {
				setProvider([searchParams.provider]);
			} else {
				setProvider(searchParams?.provider ?? []);
			}

			if (searchParams?.type && typeof searchParams?.type === 'string') {
				setType([searchParams.type]);
			} else {
				setType(searchParams?.type ?? []);
			}

			if (searchParams?.status && typeof searchParams?.status === 'string') {
				setStatus([searchParams.status]);
			} else {
				setStatus(searchParams?.status ?? []);
			}

			const initialFilters = {
				from: searchParams?.from ?? null,
				to: searchParams?.to ?? null,
				arn: searchParams?.arn ?? '',
				amount: searchParams?.amount ?? '',
				bin: searchParams?.bin ?? '',
				last4: searchParams?.last4 ?? '',
				orderId: searchParams?.orderId ?? '',
				transactionId: searchParams?.transactionId ?? '',
				externalReferenceId: searchParams?.externalReferenceId ?? '',
				query: searchParams?.query ?? '',
				pageSize: searchParams?.pageSize ?? '10',
				pageNumber: searchParams?.pageNumber ?? '1',
				provider: searchParams?.provider && typeof searchParams?.provider === 'string'
					? [searchParams.provider]
					: searchParams?.provider ?? [],
				type: searchParams?.type && typeof searchParams?.type === 'string'
					? [searchParams.type]
					: searchParams?.type ?? [],
				status: searchParams?.status && typeof searchParams?.status === 'string'
					? [searchParams.status]
					: searchParams?.status ?? [],
			};

			setAppliedFiltersState(initialFilters);

		} else {
			setAppliedFiltersState(initialFilterState);
		}

		const data = await fetchTransactionsData(localSearchParams);

		setTransactionsListData(data);

		setIsTransactionsListLoading(false);

	};

	return (
		<>
			<TransactionsPageFilter
				from={from}
				setFrom={setFrom}
				to={to}
				setTo={setTo}
				provider={provider}
				setProvider={setProvider}
				type={type}
				setType={setType}
				status={status}
				setStatus={setStatus}
				arn={arn}
				setArn={setArn}
				amount={amount}
				setAmount={setAmount}
				bin={bin}
				setBin={setBin}
				last4={last4}
				setLast4={setLast4}
				orderId={orderId}
				setOrderId={setOrderId}
				transactionId={transactionId}
				setTransactionId={setTransactionId}
				externalReferenceId={externalReferenceId}
				setExternalReferenceId={setExternalReferenceId}
				query={query}
				setQuery={setQuery}
				appliedFiltersState={appliedFiltersState}
				setAppliedFiltersState={setAppliedFiltersState}
				isTranslationDataLoading={isTransactionsListLoading}
				initialFilterState={initialFilterState}
			/>
			<Table
				name={TablesNames.transactionTableData}
				data={transactionsListData?.rows ?? []}
				isLoading={isTransactionsListLoading}
				totalItemCount={transactionsListData?.totalItemCount}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={50}
				queryParamsListener={listener}
				toolbarCustomAction={
					<ExportCSVButton
						orderTotalItemCount={transactionsListData?.totalItemCount}
						isExportCSVLoading={isExportTransactionCSVLoading}
						exportCSV={async (): Promise<void> => await exportCSVTransActionsData({
							dispatch,
							dateFilterState: [from, to],
							size: transactionsListData?.totalItemCount
						})}
					/>
				}
			/>
		</>
	);
};
